import {
  queryNearbyFeatures,
  searchFeatureLayer,
} from '../../../../../Map/useDeckGL/ClickToIdentifyGL/layerHelpers';
import type { QueryLabel } from '../../GlobalMapSearch.classify';
import {
  GIS_NAME_FIELD_PATTERNS,
  GIS_PARCEL_FIELD_PATTERNS,
  GIS_PHONE_FIELD_PATTERNS,
  GIS_SEARCH_FIELD_PATTERNS,
} from '../../GlobalMapSearch.constants';
import type {
  FeatureResult,
  MapCenter,
  ReifiedLayer,
  SearchResultItem,
} from '../../GlobalMapSearch.types';
import {
  HYDRANT_BUFFER_METERS,
  getSublayerKind,
  isProximitySearch,
} from '../../GlobalMapSearch.utils';
import {
  getViewportExtent,
  isTrafficLayer,
  shouldSkipLayer,
} from '../../GlobalMapSearch.viewport';

/** Unit of work emitted by `buildGisLayerSearchUnits`. */
export interface GisSearchUnit {
  title: string;
  run: () => Promise<void>;
}

/**
 * Stable fingerprint of a feature's attributes used to dedupe identical
 * features that the same query may return across multiple sublayer passes.
 * `_*` keys are internal annotations (`_matchedField`, etc) and `OBJECTID` /
 * `FID` are per-server identifiers — none should affect identity here.
 */
function featureFingerprint(attrs: Record<string, unknown>): string {
  const keys = Object.keys(attrs)
    .filter(k => !k.startsWith('_') && k !== 'OBJECTID' && k !== 'FID')
    .sort();
  return keys.map(k => `${k}=${attrs[k]}`).join('|');
}

/**
 * Pick the right field-pattern set + search text for a classified query.
 * Keyed off `primary` (not `query.has(...)`) because the AI sometimes
 * attaches secondary labels (e.g. `['name','parcel']`) which should not
 * collapse the search down to parcel-only.
 */
function buildSearchPasses(
  primary: QueryLabel | undefined,
  text: string,
  normalizedPhone: string | null | undefined,
): { patterns: string[]; searchText: string }[] {
  if (primary === 'phone' && normalizedPhone) {
    return [
      { patterns: GIS_PHONE_FIELD_PATTERNS, searchText: normalizedPhone },
    ];
  }
  if (primary === 'parcel') {
    return [{ patterns: GIS_PARCEL_FIELD_PATTERNS, searchText: text }];
  }
  if (primary === 'name') {
    return [{ patterns: GIS_NAME_FIELD_PATTERNS, searchText: text }];
  }
  return [{ patterns: GIS_SEARCH_FIELD_PATTERNS, searchText: text }];
}

interface LayerTaskArgs {
  kind: string;
  queryTarget: unknown;
  title: string;
  isTraffic: boolean;
  proximityMode: boolean;
  searchPasses: { patterns: string[]; searchText: string }[];
  signal: AbortSignal;
  jurisdictionBoundaryRef: React.MutableRefObject<unknown>;
  mapCenterRef: React.MutableRefObject<MapCenter | undefined>;
  seenFeatures: Set<string>;
  collected: SearchResultItem[];
  flush: () => void;
}

async function runLayerSearchTask({
  kind,
  queryTarget,
  title,
  isTraffic,
  proximityMode,
  searchPasses,
  signal,
  jurisdictionBoundaryRef,
  mapCenterRef,
  seenFeatures,
  collected,
  flush,
}: LayerTaskArgs): Promise<void> {
  try {
    if (signal.aborted) return;

    // Traffic layers are world-scale; bound them to the current viewport
    // instead of the jurisdiction polygon. If the viewport isn't available
    // yet, fall back to jurisdiction so we never silently search the entire
    // globe.
    const targetBoundary = isTraffic
      ? (getViewportExtent() ?? jurisdictionBoundaryRef.current ?? null)
      : (jurisdictionBoundaryRef.current ?? null);

    if (kind === 'hydrant' && proximityMode) {
      const center = mapCenterRef.current;
      if (!center?.latitude || !center?.longitude) return;
      const nearbyResults = await queryNearbyFeatures({
        layer: queryTarget,
        longitude: center.longitude,
        latitude: center.latitude,
        fallbackLayerName: title,
        bufferMeters: HYDRANT_BUFFER_METERS,
        maxResults: 10,
        signal,
        boundary: targetBoundary as never,
      });
      for (const r of nearbyResults) {
        const fp = featureFingerprint(r.attributes || {});
        if (seenFeatures.has(fp)) continue;
        seenFeatures.add(fp);
        collected.push({
          type: 'gis',
          data: { ...r, kind } as FeatureResult,
        });
        flush();
      }
      return;
    }

    for (const pass of searchPasses) {
      if (signal.aborted) break;
      const center = mapCenterRef.current;
      // Parcel sublayers can have many same-name matches within a city
      // (multiple Singhs, multiple Smiths, etc.) — give them more headroom
      // so the right record isn't evicted by the top-N distance cap.
      const perLayerLimit = kind === 'parcel' ? 15 : 10;
      const layerResults = await searchFeatureLayer({
        layer: queryTarget,
        searchText: pass.searchText,
        fallbackLayerName: title,
        maxResults: perLayerLimit,
        signal,
        fieldNamePatterns: pass.patterns,
        mapCenter:
          center?.latitude != null && center?.longitude != null
            ? {
                latitude: center.latitude as number,
                longitude: center.longitude as number,
              }
            : null,
        boundary: targetBoundary as never,
      });
      for (const r of layerResults) {
        const fp = featureFingerprint(r.attributes || {});
        if (seenFeatures.has(fp)) continue;
        seenFeatures.add(fp);
        collected.push({
          type: 'gis',
          data: { ...r, kind } as FeatureResult,
        });
        flush();
      }
    }
  } catch {
    // Layer search failed — swallow so one bad layer doesn't break others.
  }
}

interface BuildGisUnitsArgs {
  text: string;
  primary: QueryLabel | undefined;
  normalizedPhone: string | null;
  layers: ReifiedLayer[];
  signal: AbortSignal;
  jurisdictionBoundaryRef: React.MutableRefObject<unknown>;
  mapCenterRef: React.MutableRefObject<MapCenter | undefined>;
  collected: SearchResultItem[];
  flush: () => void;
}

/**
 * Walk every active GIS layer (and its sublayers, when present) and emit one
 * `GisSearchUnit` per searchable target. The orchestrator wires each unit's
 * `run` into its source-status tracking.
 */
export function buildGisLayerSearchUnits({
  text,
  primary,
  normalizedPhone,
  layers,
  signal,
  jurisdictionBoundaryRef,
  mapCenterRef,
  collected,
  flush,
}: BuildGisUnitsArgs): GisSearchUnit[] {
  const units: GisSearchUnit[] = [];
  const proximityMode = isProximitySearch(text);
  const searchPasses = buildSearchPasses(primary, text, normalizedPhone);
  // Shared across every layer task in this run so duplicates that surface in
  // multiple sublayers collapse to a single result row.
  const seenFeatures = new Set<string>();

  for (const layer of layers) {
    if (!layer.gisLayer) continue;

    const searchTargets: {
      kind: string;
      queryTarget: unknown;
      title: string;
      isTraffic: boolean;
    }[] = [];

    if (layer.sublayers?.length) {
      for (const sublayer of layer.sublayers) {
        if (shouldSkipLayer(sublayer.title)) continue;
        const kind = getSublayerKind(sublayer.title) || 'generic';
        searchTargets.push({
          kind,
          queryTarget: sublayer.gisSublayer || layer.gisLayer,
          title: sublayer.title,
          isTraffic: isTrafficLayer(layer.title, sublayer.title),
        });
      }
    } else {
      if (shouldSkipLayer(layer.title)) continue;
      const kind = getSublayerKind(layer.title) || 'generic';
      searchTargets.push({
        kind,
        queryTarget: layer.gisLayer,
        title: layer.title,
        isTraffic: isTrafficLayer(layer.title),
      });
    }

    for (const { kind, queryTarget, title, isTraffic } of searchTargets) {
      if (primary === 'phone' && kind === 'hydrant') continue;
      if (primary === 'parcel' && kind !== 'parcel' && kind !== 'generic')
        continue;

      units.push({
        title,
        run: () =>
          runLayerSearchTask({
            kind,
            queryTarget,
            title,
            isTraffic,
            proximityMode,
            searchPasses,
            signal,
            jurisdictionBoundaryRef,
            mapCenterRef,
            seenFeatures,
            collected,
            flush,
          }),
      });
    }
  }

  return units;
}
